import { z } from 'zod'

/**
 * Form-shaped helpers: every optional field accepts the empty string a text input
 * produces and normalises it to `undefined`. Keeping the *input* types plain strings
 * lets react-hook-form bind to them directly while zod owns the output shape.
 */

export const optionalText = (max = 255) =>
  z
    .string()
    .trim()
    .max(max, `עד ${max} תווים`)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined))

export const requiredText = (max = 255, message = 'שדה חובה') =>
  z.string().trim().min(1, message).max(max, `עד ${max} תווים`)

export const optionalEmail = () =>
  z
    .union([z.literal(''), z.string().trim().email('כתובת דואר אלקטרוני לא תקינה').max(255)])
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined))

/** Foreign key coming from a Select — the empty string means "no selection". */
export const optionalId = () =>
  z
    .string()
    .max(64)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined))

export const id = () => z.string().min(1, 'שדה חובה').max(64)

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const UTC_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d{3})?Z$/

/**
 * Calendar day as `YYYY-MM-DD` (what `<input type="date">` produces). The web forms run
 * their values through this same schema before the request is sent, so the API receives
 * the transformed `Date` serialized back to JSON — a UTC-midnight ISO string — and the
 * preprocess folds that spelling (and an in-process `Date`) back to the calendar day.
 * `''`/`null` (a cleared input) become `null`, so a partial update can tell "clear the
 * date" apart from "leave it alone" (`undefined`).
 */
export const optionalDate = () =>
  z.preprocess(
    (v) => {
      if (v instanceof Date) return v.toISOString().slice(0, 10)
      if (typeof v === 'string') {
        const utc = UTC_MIDNIGHT.exec(v.trim())
        if (utc) return utc[1]
      }
      return v
    },
    z
      .union([z.literal(''), z.null(), z.string().trim().regex(DATE_ONLY, 'תאריך לא תקין')])
      .optional()
      .transform((v) => (v ? new Date(`${v}T00:00:00.000Z`) : v === undefined ? undefined : null))
  ) as unknown as z.ZodType<Date | null | undefined, z.ZodTypeDef, string | null | undefined>

const moneyNumber = () =>
  z
    .number({ invalid_type_error: 'סכום לא תקין' })
    .multipleOf(0.01, 'עד שתי ספרות אחרי הנקודה')
    .max(1_000_000_000, 'סכום גדול מדי')

const toMoneyInput = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : typeof v === 'string' ? Number(v.trim()) : v

/** Amount in ₪ from a number input: `''` → `undefined`, string digits → number. */
export const optionalMoney = () =>
  z.preprocess(toMoneyInput, moneyNumber().nonnegative('הסכום אינו יכול להיות שלילי').optional()) as z.ZodType<
    number | undefined,
    z.ZodTypeDef,
    string | number | undefined
  >

export const requiredMoney = () =>
  z.preprocess(toMoneyInput, moneyNumber().positive('יש להזין סכום')) as z.ZodType<
    number,
    z.ZodTypeDef,
    string | number
  >

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})

export type Pagination = z.infer<typeof paginationSchema>

export type Paginated<T> = {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/** Comma-separated query param (?status=TODO,DONE) into a validated array. */
export const csvEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z.preprocess(
    (v) => {
      if (v === undefined || v === '') return undefined
      if (typeof v !== 'string') return v
      const parts = v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      return parts.length ? parts : undefined
    },
    z.array(z.enum(values)).optional()
  ) as z.ZodType<T[number][] | undefined, z.ZodTypeDef, unknown>
